import React from 'react';
import { NavLink } from 'react-router-dom';
import {
    ArrowLeft,
    Monitor,
    Smartphone,
    Search,
    Lock,
    Bot,
    ArrowRight,
    CheckCircle2,
    Sparkles,
    Terminal,
    Zap,
    ShieldCheck,
    Cpu,
} from 'lucide-react';

const Runner: React.FC = () => {
    return (
        <div className="min-h-screen bg-background-dark text-white font-sans selection:bg-primary/30">
            <style>{`
                .font-montserrat { font-family: 'Montserrat', sans-serif; }
                .card-hover { transition: transform 0.3s ease, box-shadow 0.3s ease; }
                .card-hover:hover { transform: translateY(-4px); box-shadow: 0 20px 40px rgba(59,130,246,0.1); }
                .glow-blue { box-shadow: 0 0 40px rgba(59, 130, 246, 0.15); }
            `}</style>

            {/* Navigation */}
            <nav className="fixed top-0 w-full z-50 bg-background-dark/80 backdrop-blur-md border-b border-white/5">
                <div className="max-w-7xl mx-auto px-4 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <NavLink to="/" className="text-slate-400 hover:text-white transition-colors flex items-center gap-2">
                            <ArrowLeft className="w-4 h-4" /> Volver
                        </NavLink>
                        <div className="h-6 w-px bg-white/10"></div>
                        <img src="/logo.png" alt="FlowSpec QA" className="h-8 w-auto opacity-80" />
                    </div>
                    <NavLink
                        to="/"
                        onClick={() => setTimeout(() => document.querySelector('#partners')?.scrollIntoView({ behavior: 'smooth' }), 100)}
                        className="px-5 py-2.5 bg-primary hover:bg-primary-hover rounded-xl text-sm font-bold transition-all shadow-lg shadow-primary/25"
                    >
                        Agendar Demo
                    </NavLink>
                </div>
            </nav>

            {/* Hero */}
            <section className="pt-40 pb-24 px-4 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-indigo-600/5 pointer-events-none" />
                <div className="max-w-5xl mx-auto text-center relative z-10">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-bold mb-6 font-montserrat tracking-widest uppercase">
                        <Terminal className="w-4 h-4" />
                        FlowSpecQA Runner
                    </div>
                    <h1 className="text-4xl md:text-6xl font-black mb-6 tracking-tight leading-[1.1] font-montserrat">
                        El primer Test Runner con{' '}
                        <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary via-blue-400 to-indigo-400">
                            MCP nativo.
                        </span>
                    </h1>
                    <p className="text-lg md:text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed mb-10">
                        Genera, descubre y ejecuta pruebas Web y Mobile — directamente desde la máquina de tu equipo,
                        con integración nativa a agentes de IA. <span className="text-white font-semibold">Sin exponer credenciales. Sin configurar infraestructura.</span>
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
                        <NavLink
                            to="/"
                            className="px-10 py-4 bg-primary hover:bg-primary-hover rounded-2xl font-bold text-lg shadow-2xl shadow-primary/40 transition-all hover:-translate-y-1 flex items-center gap-2"
                        >
                            Agendar Demo del Runner <ArrowRight className="w-5 h-5" />
                        </NavLink>
                    </div>
                    {/* Hero Image */}
                    <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-primary/20 glow-blue">
                        <div className="absolute inset-0 bg-gradient-to-t from-background-dark/60 via-transparent to-transparent z-10 pointer-events-none" />
                        <img src="/runner/runner-hero.jpg" alt="FlowSpecQA Runner — Web y Mobile" className="w-full object-cover" />
                    </div>
                </div>
            </section>

            {/* 5 Capabilities */}
            <section className="py-20 px-4 border-y border-white/5 bg-white/[0.02]">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-black font-montserrat mb-4">5 capacidades. Una sola herramienta.</h2>
                        <p className="text-slate-400 max-w-xl mx-auto">Nada en el mercado QA actual combina estas cinco capacidades en un solo runner.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                        {/* Web */}
                        <div className="p-8 rounded-3xl bg-blue-500/5 border border-blue-500/20 card-hover">
                            <div className="w-14 h-14 bg-blue-500/10 rounded-2xl flex items-center justify-center mb-6">
                                <Monitor className="w-7 h-7 text-blue-400" />
                            </div>
                            <div className="flex items-center gap-2 mb-3">
                                <h3 className="text-xl font-bold font-montserrat">Automatización Web</h3>
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-500/10 border border-blue-500/20 text-blue-400 uppercase tracking-widest">Playwright</span>
                            </div>
                            <p className="text-slate-400 text-sm leading-relaxed mb-5">
                                Ejecuta suites completas en Chrome, Firefox y Safari. Scripts generados por IA con selectores semánticos robustos y Page Object Model.
                            </p>
                            <ul className="space-y-2">
                                {["Multi-browser en paralelo", "Capturas y video de cada ejecución", "Reportes HTML automáticos"].map((f, i) => (
                                    <li key={i} className="flex items-center gap-2 text-xs text-slate-300">
                                        <CheckCircle2 className="w-3.5 h-3.5 text-blue-400 shrink-0" /> {f}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Mobile */}
                        <div className="p-8 rounded-3xl bg-purple-500/5 border border-purple-500/20 card-hover">
                            <div className="w-14 h-14 bg-purple-500/10 rounded-2xl flex items-center justify-center mb-6">
                                <Smartphone className="w-7 h-7 text-purple-400" />
                            </div>
                            <div className="flex items-center gap-2 mb-3">
                                <h3 className="text-xl font-bold font-montserrat">Automatización Mobile</h3>
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-purple-500/10 border border-purple-500/20 text-purple-400 uppercase tracking-widest">Maestro</span>
                            </div>
                            <p className="text-slate-400 text-sm leading-relaxed mb-5">
                                Flujos YAML para iOS y Android ejecutados directamente en emuladores o dispositivos físicos de tu equipo. Sin SaaS de terceros.
                            </p>
                            <ul className="space-y-2">
                                {["Apps nativas, híbridas y React Native", "Emuladores y dispositivos reales", "Integración con pipelines CI/CD"].map((f, i) => (
                                    <li key={i} className="flex items-center gap-2 text-xs text-slate-300">
                                        <CheckCircle2 className="w-3.5 h-3.5 text-purple-400 shrink-0" /> {f}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Discovery */}
                        <div className="rounded-3xl bg-emerald-500/5 border border-emerald-500/20 card-hover overflow-hidden">
                            <div className="relative">
                                <img src="/runner/runner-discovery.jpg" alt="Discovery Mode" className="w-full object-cover h-44" />
                                <div className="absolute inset-0 bg-gradient-to-t from-background-dark via-background-dark/40 to-transparent" />
                            </div>
                            <div className="p-8 pt-4">
                                <div className="w-14 h-14 bg-emerald-500/10 rounded-2xl flex items-center justify-center mb-6">
                                    <Search className="w-7 h-7 text-emerald-400" />
                                </div>
                                <div className="flex items-center gap-2 mb-3">
                                    <h3 className="text-xl font-bold font-montserrat">Discovery Mode</h3>
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 uppercase tracking-widest">IA</span>
                                </div>
                                <p className="text-slate-400 text-sm leading-relaxed mb-5">
                                    Apunta el Runner a tu app y déjalo explorar. Descubre flujos, mapea interacciones y genera automáticamente el plan de pruebas y la suite de automatización.
                                </p>
                                <ul className="space-y-2">
                                    {["Exploración autónoma de la app", "Genera plan de pruebas completo", "Suite de automatización lista para ejecutar"].map((f, i) => (
                                        <li key={i} className="flex items-center gap-2 text-xs text-slate-300">
                                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> {f}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Local Execution */}
                        <div className="rounded-3xl bg-amber-500/5 border border-amber-500/20 card-hover overflow-hidden">
                            <div className="relative">
                                <img src="/runner/runner-local.jpg" alt="Ejecución Local Segura" className="w-full object-cover h-52" />
                                <div className="absolute inset-0 bg-gradient-to-t from-background-dark via-background-dark/30 to-transparent" />
                            </div>
                            <div className="p-8 pt-4">
                            <div className="w-14 h-14 bg-amber-500/10 rounded-2xl flex items-center justify-center mb-6">
                                <Lock className="w-7 h-7 text-amber-400" />
                            </div>
                            <div className="flex items-center gap-2 mb-3">
                                <h3 className="text-xl font-bold font-montserrat">Ejecución Local</h3>
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/20 text-amber-400 uppercase tracking-widest">On-Device</span>
                            </div>
                            <p className="text-slate-400 text-sm leading-relaxed mb-5">
                                Corre directamente en la máquina de tu QA o desarrollador. Las credenciales, datos de prueba y accesos internos <strong className="text-white">nunca salen de tu organización</strong>. Sin VPN, sin whitelist, sin configuración de red.
                            </p>
                            <ul className="space-y-2">
                                {[
                                    "Credenciales 100% locales y seguras",
                                    "Acceso a ambientes internos sin exposición",
                                    "Cumplimiento con políticas corporativas (banca, salud, gobierno)",
                                    "Sin dependencia de infraestructura cloud externa"
                                ].map((f, i) => (
                                    <li key={i} className="flex items-center gap-2 text-xs text-slate-300">
                                        <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 shrink-0" /> {f}
                                    </li>
                                ))}
                            </ul>
                            </div>
                        </div>

                        {/* MCP Nativo */}
                        <div className="rounded-3xl bg-gradient-to-br from-primary/10 to-indigo-600/10 border border-primary/30 card-hover relative overflow-hidden">
                            <div className="absolute top-3 right-3 z-20 text-[10px] font-black border border-primary/30 px-2 py-0.5 rounded-full text-primary tracking-tighter animate-pulse bg-background-dark/60 backdrop-blur-sm">
                                ÚNICO EN EL MERCADO
                            </div>
                            <div className="relative">
                                <img src="/runner/runner-mcp.jpg" alt="MCP Nativo" className="w-full object-cover h-52" />
                                <div className="absolute inset-0 bg-gradient-to-t from-background-dark via-background-dark/30 to-transparent" />
                            </div>
                            <div className="p-8 pt-4">
                            <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-6">
                                <Bot className="w-7 h-7 text-primary" />
                            </div>
                            <div className="flex items-center gap-2 mb-3">
                                <h3 className="text-xl font-bold font-montserrat">MCP Nativo</h3>
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-primary/10 border border-primary/20 text-primary uppercase tracking-widest">Model Context Protocol</span>
                            </div>
                            <p className="text-slate-400 text-sm leading-relaxed mb-5">
                                FlowSpecQA Runner tiene su propio servidor MCP. Esto significa que agentes de IA como Claude pueden <strong className="text-white">ejecutar, monitorear y analizar tus pruebas directamente</strong> usando lenguaje natural — sin escribir un solo comando.
                            </p>
                            <ul className="space-y-2">
                                {[
                                    "\"Claude, ejecuta las pruebas de login\" — y listo",
                                    "El agente interpreta resultados y sugiere correcciones",
                                    "Integración nativa con flujos de desarrollo AI-first",
                                    "Primera herramienta QA verdaderamente AI-native"
                                ].map((f, i) => (
                                    <li key={i} className="flex items-center gap-2 text-xs text-slate-300">
                                        <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0" /> {f}
                                    </li>
                                ))}
                            </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section className="py-24 px-4">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-black font-montserrat mb-4">Cómo funciona</h2>
                        <p className="text-slate-400">De cero a suite automatizada en minutos.</p>
                    </div>
                    <div className="relative">
                        <div className="hidden md:block absolute left-8 top-0 bottom-0 w-px bg-gradient-to-b from-primary/50 via-primary/20 to-transparent" />
                        <div className="space-y-10">
                            {[
                                {
                                    icon: <Cpu className="w-5 h-5 text-primary" />,
                                    step: "01",
                                    title: "Instala el Runner localmente",
                                    desc: "Una instalación simple en la máquina del QA o desarrollador. Compatible con macOS, Windows y Linux. Sin configuración de servidores ni infraestructura cloud."
                                },
                                {
                                    icon: <Search className="w-5 h-5 text-emerald-400" />,
                                    step: "02",
                                    title: "Discovery: explora tu app automáticamente",
                                    desc: "Apunta el Runner a tu app web o mobile. El modo Discovery navega de forma autónoma, mapea flujos, detecta interacciones y genera el plan de pruebas completo."
                                },
                                {
                                    icon: <Sparkles className="w-5 h-5 text-indigo-400" />,
                                    step: "03",
                                    title: "Genera la suite de automatización",
                                    desc: "A partir del plan descubierto, la IA genera scripts Playwright (Web) o flujos Maestro (Mobile) listos para ejecutar, con cobertura de casos positivos, negativos y de borde."
                                },
                                {
                                    icon: <Zap className="w-5 h-5 text-yellow-400" />,
                                    step: "04",
                                    title: "Ejecuta — vía Runner o vía Claude",
                                    desc: "Ejecuta directamente desde el Runner o usa el MCP para pedirle a Claude que lo haga por ti. Resultados, capturas y logs en tiempo real. Todo local, todo seguro."
                                },
                                {
                                    icon: <ShieldCheck className="w-5 h-5 text-amber-400" />,
                                    step: "05",
                                    title: "Reportes y trazabilidad garantizada",
                                    desc: "Cada ejecución genera evidencia completa: capturas de pantalla, video, logs y trazas. Audit-ready desde el primer día, sin datos expuestos al exterior."
                                }
                            ].map((s, i) => (
                                <div key={i} className="flex gap-8 items-start">
                                    <div className="shrink-0 w-16 h-16 rounded-2xl bg-background-dark border border-white/10 flex items-center justify-center relative z-10">
                                        {s.icon}
                                    </div>
                                    <div className="pt-2">
                                        <div className="text-[10px] text-slate-600 uppercase tracking-widest font-bold mb-1">Paso {s.step}</div>
                                        <h3 className="text-lg font-bold font-montserrat mb-2">{s.title}</h3>
                                        <p className="text-slate-400 text-sm leading-relaxed max-w-2xl">{s.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* vs Traditional */}
            <section className="py-20 px-4 bg-white/[0.02] border-y border-white/5">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-black font-montserrat mb-4">Por qué es diferente</h2>
                        <p className="text-slate-400">Lo que ningún otro runner del mercado ofrece hoy.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="p-8 rounded-3xl bg-white/5 border border-white/5">
                            <h3 className="text-lg font-bold mb-6 text-slate-400 uppercase tracking-widest text-xs">Runners tradicionales</h3>
                            <ul className="space-y-4">
                                {[
                                    "Requieren infraestructura cloud (BrowserStack, Sauce Labs)",
                                    "Credenciales y datos expuestos a servidores externos",
                                    "Sin Discovery — debes escribir todos los tests manualmente",
                                    "Sin integración con agentes de IA",
                                    "Solo Web o solo Mobile, nunca ambos",
                                    "Sin generación automática de suites de automatización",
                                ].map((f, i) => (
                                    <li key={i} className="flex items-center gap-3 text-sm text-slate-500">
                                        <span className="w-4 h-4 rounded-full border border-slate-700 flex items-center justify-center shrink-0 text-slate-600 text-xs">✕</span> {f}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="p-8 rounded-3xl bg-primary/5 border border-primary/20">
                            <h3 className="text-lg font-bold mb-6 text-primary uppercase tracking-widest text-xs">FlowSpecQA Runner</h3>
                            <ul className="space-y-4">
                                {[
                                    "Corre 100% local en la máquina de tu equipo",
                                    "Credenciales y datos nunca salen de tu organización",
                                    "Discovery autónomo — genera el plan de pruebas solo",
                                    "MCP nativo — Claude ejecuta tus pruebas por ti",
                                    "Web + Mobile + Discovery en una sola herramienta",
                                    "Suite de automatización generada automáticamente por IA",
                                ].map((f, i) => (
                                    <li key={i} className="flex items-center gap-3 text-sm text-slate-200">
                                        <CheckCircle2 className="w-4 h-4 text-primary shrink-0" /> {f}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 px-4">
                <div className="max-w-3xl mx-auto text-center p-12 bg-gradient-to-br from-primary/10 to-indigo-600/10 border border-primary/30 rounded-3xl shadow-2xl shadow-primary/10">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-bold mb-6 font-montserrat tracking-widest uppercase">
                        <Sparkles className="w-4 h-4" />
                        Acceso anticipado disponible
                    </div>
                    <h2 className="text-2xl md:text-4xl font-black mb-4 font-montserrat">¿Listo para ver el Runner en acción?</h2>
                    <p className="text-slate-400 mb-8 leading-relaxed">
                        Agenda una demo personalizada y te mostramos Discovery, ejecución local y el MCP funcionando en tiempo real con tu stack.
                    </p>
                    <NavLink
                        to="/"
                        className="inline-flex items-center gap-2 px-10 py-4 bg-primary hover:bg-primary-hover rounded-2xl font-bold text-lg shadow-2xl shadow-primary/40 transition-all hover:-translate-y-1"
                    >
                        Agendar Demo del Runner <ArrowRight className="w-5 h-5" />
                    </NavLink>
                    <p className="text-xs text-slate-600 mt-5 italic">Sin compromiso · Demo personalizada con tu stack</p>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 text-center text-slate-600 text-sm border-t border-white/5">
                <p>© 2026 IAFlows. FlowSpec QA Runner. <NavLink to="/privacy" className="hover:text-slate-400 transition-colors">Privacidad</NavLink> · <NavLink to="/security" className="hover:text-slate-400 transition-colors">Security First</NavLink></p>
            </footer>
        </div>
    );
};

export default Runner;
